import { usePublicProjectReader } from '@/lib/public-project-reader';
import { useState, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import {
    Paperclip,
    Upload,
    Download,
    Trash2,
    FileText,
    FileImage,
    FileVideo,
    FileAudio,
    File,
    X,
    Loader2,
    ExternalLink,
} from 'lucide-react';
import {
    useAttachments,
    useCreateAttachment,
    useDeleteAttachment,
    uploadFile,
} from '@/hooks/use-attachments';
import { useAuthStore } from '@/stores/auth-store';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import type { Attachment } from '@/lib/schemas';

// ============================================
// HELPERS
// ============================================

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getFileIcon(mimeType: string) {
    if (mimeType.startsWith('image/')) return FileImage;
    if (mimeType.startsWith('video/')) return FileVideo;
    if (mimeType.startsWith('audio/')) return FileAudio;
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return FileText;
    return File;
}

function isImageFile(mimeType: string): boolean {
    return mimeType.startsWith('image/');
}

// ============================================
// ATTACHMENT ITEM
// ============================================

interface AttachmentItemProps {
    canEdit: boolean;
    attachment: Attachment;
    onDelete: (attachmentId: string) => void;
    onPreview: (attachment: Attachment) => void;
}

function AttachmentItem({ attachment, onDelete, onPreview, canEdit }: AttachmentItemProps) {
    const { user } = useAuthStore();
    const { t, language } = useI18n();
    const reader = usePublicProjectReader();
    const isUploader = canEdit && user?.id === attachment.uploadedById;
    const isImage = isImageFile(attachment.mimeType);
    const FileIcon = getFileIcon(attachment.mimeType);
    const dateLocale = language === 'fr' ? fr : enUS;

    // Use direct download endpoint through the API
    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
    const handleDownload = useCallback(() => {
        // Use the direct file download endpoint
        window.open(reader ? attachment.url : `${API_BASE_URL}/files/${attachment.filename}`, '_blank', 'noopener,noreferrer');
    }, [attachment.filename, attachment.url, reader, API_BASE_URL]);

    return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors group">
            {/* Preview/Icon */}
            <div
                className="flex-shrink-0 cursor-pointer"
                onClick={() => isImage && onPreview(attachment)}
            >
                {isImage ? (
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-background">
                        <img
                            src={attachment.url}
                            alt={attachment.originalName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </div>
                ) : (
                    <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                        <FileIcon className="h-6 w-6 text-primary" />
                    </div>
                )}
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate" title={attachment.originalName}>
                    {attachment.originalName}
                </p>
                <p className="text-xs text-foreground-muted">
                    {formatFileSize(attachment.size)} • {format(new Date(attachment.createdAt), 'MMM d, yyyy', { locale: dateLocale })}
                </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleDownload}
                    title={t('attachments.download')}
                >
                    <Download className="h-4 w-4" />
                </Button>
                {isUploader && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(attachment.id)}
                        title={t('common.delete')}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

// ============================================
// UPLOAD PROGRESS
// ============================================

interface UploadProgressProps {
    filename: string;
    progress: number;
    onCancel: () => void;
}

function UploadProgress({ filename, progress, onCancel }: UploadProgressProps) {
    return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-surface">
            <div className="flex-shrink-0 w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>

            <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{filename}</p>
                <div className="mt-1.5">
                    <div className="h-1.5 w-full bg-surface-hover rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-xs text-foreground-muted mt-1">{progress}% uploaded</p>
                </div>
            </div>

            <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onCancel}
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
}

// ============================================
// IMAGE PREVIEW DIALOG
// ============================================

interface ImagePreviewDialogProps {
    attachment: Attachment | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

function ImagePreviewDialog({ attachment, open, onOpenChange }: ImagePreviewDialogProps) {
    const { t } = useI18n();
    if (!attachment) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="truncate">{attachment.originalName}</DialogTitle>
                </DialogHeader>
                <div className="flex items-center justify-center max-h-[70vh] overflow-hidden">
                    <img
                        src={attachment.url}
                        alt={attachment.originalName}
                        className="max-w-full max-h-[70vh] object-contain rounded-md"
                    />
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        {t('attachments.openInNewTab')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ============================================
// ATTACHMENTS SECTION
// ============================================

interface AttachmentsSectionProps {
    canEdit?: boolean;
    taskId: string;
}

export function AttachmentsSection({ taskId, canEdit = true }: AttachmentsSectionProps) {
    const { t } = useI18n();
    const { data, isLoading, error } = useAttachments(taskId);
    const createAttachment = useCreateAttachment(taskId);
    const deleteAttachment = useDeleteAttachment(taskId);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFilename, setUploadingFilename] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset input
        e.target.value = '';

        // Validate file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
            alert('File size must be less than 50MB');
            return;
        }

        setUploading(true);
        setUploadingFilename(file.name);
        setUploadProgress(0);

        try {
            // Upload file to storage
            const uploadResult = await uploadFile(file, (progress) => {
                setUploadProgress(progress);
            });

            // Create attachment record
            await createAttachment.mutateAsync({
                filename: uploadResult.filename,
                originalName: uploadResult.originalName,
                mimeType: uploadResult.mimeType,
                size: uploadResult.size,
            });
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file. Please try again.');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            setUploadingFilename('');
        }
    }, [createAttachment]);

    const handleDeleteClick = useCallback((attachmentId: string) => {
        setAttachmentToDelete(attachmentId);
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!attachmentToDelete) return;

        try {
            await deleteAttachment.mutateAsync(attachmentToDelete);
            setDeleteDialogOpen(false);
            setAttachmentToDelete(null);
        } catch (error) {
            console.error('Error deleting attachment:', error);
        }
    }, [attachmentToDelete, deleteAttachment]);

    const handlePreview = useCallback((attachment: Attachment) => {
        setPreviewAttachment(attachment);
        setPreviewOpen(true);
    }, []);

    const attachments = data?.attachments || [];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-foreground-secondary">
                    <Paperclip className="h-4 w-4" />
                    <h3 className="font-medium text-sm">
                        {t('tasks.attachments')} {attachments.length > 0 && `(${attachments.length})`}
                    </h3>
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || !canEdit}
                >
                    <Upload className="h-4 w-4 mr-1" />
                    {t('attachments.upload')}
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept="*/*"
                />
            </div>

            {/* Upload progress */}
            {uploading && (
                <UploadProgress
                    filename={uploadingFilename}
                    progress={uploadProgress}
                    onCancel={() => setUploading(false)}
                />
            )}

            {/* Attachments list */}
            <div className="space-y-2">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : error ? (
                    <div className="text-center py-8 text-destructive text-sm">
                        {t('attachments.failedToLoad')}
                    </div>
                ) : attachments.length === 0 ? (
                    <div className="text-center py-8 text-foreground-muted text-sm border border-dashed border-border rounded-lg">
                        {t('attachments.noAttachments')} {t('attachments.noAttachmentsHint')}
                    </div>
                ) : (
                    attachments.map((attachment) => (
                        <AttachmentItem
                            canEdit={canEdit}
                            key={attachment.id}
                            attachment={attachment}
                            onDelete={handleDeleteClick}
                            onPreview={handlePreview}
                        />
                    ))
                )}
            </div>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('attachments.deleteTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('attachments.deleteConfirm')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deleteAttachment.isPending}
                        >
                            {deleteAttachment.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                t('common.delete')
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Image preview dialog */}
            <ImagePreviewDialog
                attachment={previewAttachment}
                open={previewOpen}
                onOpenChange={setPreviewOpen}
            />
        </div>
    );
}
